import ArrowBack from '@material-ui/icons/ArrowBack';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { OrganizationScopedFolderIcon } from '@monorepo/shared/componentsV2/documents/OrganizationScopedFolderIcon';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { DocumentTitle } from '@monorepo/shared/componentsV2/DocumentTitle';
import { DocumentUploadFileInput } from '@monorepo/shared/componentsV2/documents/DocumentUploadFileInput';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { Search } from '@monorepo/shared/componentsV2/Search';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { isOrganizationScopedFolder } from '@monorepo/shared/utils/documentFolderScope';
import { DocumentFolderWithFileCountResponse } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useSiteDocumentsLink } from '../../hooks/useSiteDocumentsLinks';
import { useUploadSiteDocuments } from '../../hooks/useUploadSiteDocuments';

const SearchContainer = styled.div`
  max-width: 10rem;
`;

const TitleWithIcon = styled.span`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
`;

interface SiteFolderDocumentsHeaderProps {
  organizationId: string;
  projectId: string;
  folder: DocumentFolderWithFileCountResponse;
  filter: string;
  onSearch: (search: string) => void;
  canEditSiteDocuments: boolean;
}

export function SiteFolderDocumentsHeader({
  organizationId,
  projectId,
  folder,
  filter,
  onSearch,
  canEditSiteDocuments,
}: SiteFolderDocumentsHeaderProps) {
  const documentsLink = useSiteDocumentsLink();
  const navigate = useNavigate();
  const isOrganizationScoped = isOrganizationScopedFolder(folder.scope);

  const [upload, { status }] = useUploadSiteDocuments({
    config: { throwOnError: true },
  });
  const isUploading = status === 'loading';

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value),
    [onSearch],
  );

  const onUpload = useCallback(
    (files: File[]) =>
      upload({ organizationId, projectId, folderId: folder.id, files }),
    [folder.id, organizationId, projectId, upload],
  );

  // Org-scoped folder files are managed in Setup.
  const canUploadHere = canEditSiteDocuments && !isOrganizationScoped;

  return (
    <>
      {isOrganizationScoped && <DocumentTitle title={folder.name} />}
      <Header
        title={
          isOrganizationScoped ? (
            <TitleWithIcon>
              {folder.name}
              <OrganizationScopedFolderIcon size="large" />
            </TitleWithIcon>
          ) : (
            folder.name
          )
        }
        variant={isOrganizationScoped ? 'bottomBorder' : 'noDropShadow'}
        leftActions={
          <IconButton onClick={() => navigate(documentsLink)}>
            <ArrowBack />
            <VisuallyHidden>Back to documents</VisuallyHidden>
          </IconButton>
        }
        rightActions={
          <ButtonsGroup>
            <SearchContainer>
              <Search onChange={handleSearch} value={filter} />
            </SearchContainer>
            {canUploadHere && (
              <DocumentUploadFileInput
                inputId="site-folder-documents-upload"
                uploadButtonText="Add documents"
                isUploading={isUploading}
                onUpload={onUpload}
              />
            )}
          </ButtonsGroup>
        }
      />
    </>
  );
}
